import { createSlice, createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { TAccessStatus } from '@/shared/models/access-status';
import { EntityPage } from '@/shared/models/entity-page';
import { LicensingAuthorityTypes } from '@/shared/models/licensingAuthority';

import type { CarModels, CarFull, Car } from '../model';
import { VehicleUpdateForm } from '../model/car-form';
import { namespace as carsNamespace } from '../namespace';

const carEntityAdapter = createEntityAdapter<CarFull>({});

const defaultFormValues: VehicleUpdateForm & { accessStatus: TAccessStatus } = {
  id: '',
  make: 'Mercedes-Benz',
  model: '',
  generation: '',
  color: '',
  interiorColor: '',
  registrationDate: null,
  year: '',
  licensePlate: '',
  licenseNumber: '',
  accessStatus: 'expired_docs',
  licensingAuthority: LicensingAuthorityTypes.TFL,
  wheelbase: '',
  vin: '',
};

const mapCarToForm = (car: CarFull): VehicleUpdateForm & { accessStatus: TAccessStatus } => ({
  make: car.make,
  model: car.model,
  generation: car.generation,
  color: car.color,
  interiorColor: car.interiorColor,
  year: car.year,
  licensePlate: car.licensePlate,
  licenseNumber: car.licenseNumber,
  registrationDate: car.registrationDate,
  id: car.id,
  accessStatus: car.accessStatus,
  licensingAuthority: car.licensingAuthority ?? LicensingAuthorityTypes.TFL,
  wheelbase: car.wheelbase,
  vin: car.vin,
  engineType: car.engineType,
});

const entityState = carEntityAdapter.getInitialState();

interface TState extends EntityState<CarFull> {
  models: Array<CarModels>;
  all: Car[];
  form: VehicleUpdateForm & { accessStatus: TAccessStatus };
  cars: EntityPage<Car>;
}

const initialState: TState = {
  models: [],
  all: [],
  cars: {
    items: [],
    total: 0,
    limit: 20,
    offset: 0,
  },
  form: { ...defaultFormValues },
  ...entityState,
};

const carsSlice = createSlice({
  name: carsNamespace,
  initialState,
  reducers: {
    setAllCars: (state, action: PayloadAction<Car[]>) => {
      state.all = action.payload;
    },
    setCars: (state, action: PayloadAction<EntityPage<Car>>) => {
      state.cars = action.payload;
    },
    setModels: (state, action: PayloadAction<{ models: CarModels[] }>) => {
      state.models = action.payload.models;
    },
    setCar: (state, action: PayloadAction<CarFull>) => {
      state.entities[action.payload.id] = action.payload;

      const idx = state.all.findIndex(item => item.id === action.payload.id);

      if (idx === -1) {
        state.all.push(action.payload);
      } else {
        state.all[idx] = action.payload;
      }
    },
    deleteCarFromStore: (state, action: PayloadAction<{ carId: string }>) => {
      state.entities = {};
      state.all = state.all.filter(car => car.id !== action.payload.carId);
      state.form = { ...defaultFormValues };
    },
    updateForm: (state, action: PayloadAction<CarFull | undefined>) => {
      state.form = action.payload ? mapCarToForm(action.payload) : defaultFormValues;
    },
    resetCarForm: state => {
      state.form = { ...defaultFormValues };
    },
  },
});

export { carsSlice, carsNamespace };
